import { useTranslation } from 'react-i18next';
import { examplesFor, pickLang, LangStr } from '@/lib/agentHelp';

/**
 * Click-to-fill suggestion chips. Lowers the "blank page" problem — a user
 * clicks a chip and the input is filled with a realistic starter they can edit.
 *
 * By default it pulls the corpus-grounded example prompts for `page`
 * (`groupKey` lets Audit swap per compliance scope). Pass `items` to supply a
 * custom list instead (e.g. common audiences). Labels render in the app's UI
 * language (EN/JA shipped, English fallback otherwise).
 */
export function ExampleChips({
  page,
  groupKey = 'default',
  items,
  label,
  onPick,
}: {
  page?: string;
  groupKey?: string;
  items?: LangStr[];
  label?: string;
  onPick: (text: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const examples = items ?? (page ? examplesFor(page, groupKey) : []);
  if (examples.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-[0.14em] text-ink-400 font-semibold">
        {label ?? (t('help.try', { defaultValue: 'Not sure what to ask? Try one of these' }) as string)}
      </div>
      <div className="flex flex-wrap gap-2">
        {examples.map((ex, i) => {
          const label = pickLang(ex, i18n.language);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPick(label)}
              className="text-left text-[12.5px] text-ink-700 bg-paper-100 hover:bg-accent-50 hover:text-accent-700 border border-ink-100 hover:border-accent-200 rounded-full px-3 py-1.5 transition"
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
